"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import {
  Mic,
  Square,
  Loader2,
  FileText,
  RefreshCw,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  X,
  Settings2,
  Clock,
} from "lucide-react";
import { useAuth } from "@workos-inc/authkit-react";
import { useToast } from "../hooks/use-toast";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import api from "../http";
import { useSearchParams, useNavigate } from "react-router";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MeetingTemplate {
  id: string;
  name: string;
  content: string;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

interface TranscriptSegment {
  id?: string;
  start?: number;
  end?: number;
  text: string;
  speaker?: string;
}

interface Transcript {
  text: string;
  duration?: number;
  segments: TranscriptSegment[];
}

interface MeetingNote {
  id: string;
  title: string;
  template_id: string | null;
  transcript?: Transcript;
  generated_note?: string;
  audio_duration: number | null;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

export default function Meeting() {
  const { getAccessToken, user, role } = useAuth();
  const { toast } = useToast();
  const { themeColor, logoName } = useColor();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;
  const APP_NAME = appConfig.features.isDemoMode ? logoName : appConfig.name;
  const isSuperadmin = role === "superadmin";

  // User state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Note state (sidebar now handled by MemberSidebar)
  const [, setSelectedNoteId] = useState<string | null>(null);
  const [selectedNote, setSelectedNote] = useState<MeetingNote | null>(null);
  const [isLoadingNoteDetail, setIsLoadingNoteDetail] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Transcript and note state
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [generatedNote, setGeneratedNote] = useState<string>("");
  const [isGeneratingNote, setIsGeneratingNote] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState("");

  // Template state
  const [templates, setTemplates] = useState<MeetingTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);

  // Language state
  const [selectedLanguage, setSelectedLanguage] = useState<string>("de");
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languages = [
    { code: "de", name: "Deutsch" },
    { code: "en", name: "English" },
    { code: "it", name: "Italiano" },
    { code: "fr", name: "Français" },
  ];

  // Template Manager state (superadmin only)
  const [isTemplateManagerOpen, setIsTemplateManagerOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MeetingTemplate | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState<string | null>(null);


  // Browser compatibility utilities
  const getSupportedMimeType = (): string | null => {
    const mimeTypes = [
      "audio/webm;codecs=opus",
      "audio/webm;codecs=vorbis",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
      "audio/wav",
    ];
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }
    return null;
  };

  const isMediaRecorderSupported = (): boolean => {
    return (
      typeof MediaRecorder !== "undefined" &&
      typeof MediaRecorder.isTypeSupported === "function"
    );
  };

  const isGetUserMediaSupported = (): boolean => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  };

  const getAudioConstraints = () => {
    const constraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (!isSafari) {
      constraints.deviceId = { ideal: "default" };
    }
    return constraints;
  };

  const isMicrophoneSupported = (): boolean => {
    return isGetUserMediaSupported() && isMediaRecorderSupported();
  };

  // Load current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };
    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  // Handle profile image update
  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  // Load note detail when selected
  const loadNoteDetail = useCallback(async (noteId: string) => {
    setIsLoadingNoteDetail(true);
    try {
      const token = await getAccessToken();
      const data = await api.getMeetingNote(noteId, token);
      setSelectedNote(data.meeting_note);
    } catch (error) {
      console.error("Failed to load note detail:", error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingNoteDetail(false);
    }
  }, [getAccessToken, toast]);

  // Handle URL params for note selection or new meeting
  useEffect(() => {
    const noteId = searchParams.get("note");
    const isNew = searchParams.get("new");
    
    if (isNew === "true") {
      // Reset state for new meeting
      setSelectedNoteId(null);
      setSelectedNote(null);
      setTranscript(null);
      setGeneratedNote("");
      setMeetingTitle("");
      setRecordingDuration(0);
      audioChunksRef.current = [];
    } else if (noteId) {
      setSelectedNoteId(noteId);
      loadNoteDetail(noteId);
    }
  }, [searchParams, loadNoteDetail]);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const token = await getAccessToken();
        const data = await api.getMeetingTemplates(token);
        setTemplates(data.templates || []);
      } catch (error) {
        console.error("Failed to load meeting templates:", error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, [getAccessToken]);

  // Start new meeting (deselect note and navigate to clean meeting page)
  const handleNewMeeting = () => {
    setSelectedNoteId(null);
    setSelectedNote(null);
    resetMeeting();
    navigate("/meeting");
  };

  // Start recording
  const startRecording = async () => {
    if (!isMicrophoneSupported()) {
      toast({
        title: "Nicht unterstützt",
        description: "Sprachaufnahme wird in Ihrem Browser nicht unterstützt.",
        variant: "destructive",
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: getAudioConstraints(),
      });

      const mimeType = getSupportedMimeType();
      if (!mimeType) {
        toast({
          title: "Fehler",
          description: "Kein unterstütztes Audioformat gefunden.",
          variant: "destructive",
        });
        return;
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        if (audioBlob.size > 0) {
          await processRecording(audioBlob);
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingDuration(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      toast({
        title: "Fehler",
        description: "Mikrofon konnte nicht gestartet werden.",
        variant: "destructive",
      });
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Process the recording - transcribe and auto-generate note
  const processRecording = async (audioBlob: Blob) => {
    setIsProcessingRecording(true);
    setIsGeneratingNote(true);
    
    try {
      const token = await getAccessToken();
      
      // Step 1: Transcribe
      const transcriptResult = await api.transcribeMeeting(audioBlob, selectedLanguage, token);
      const transcriptData = transcriptResult.transcript;
      
      if (!transcriptData?.text) {
        throw new Error("Transkription leer");
      }
      
      const title = `Meeting - ${new Date().toLocaleDateString("de-DE")}`;
      setMeetingTitle(title);
      
      // Step 2: Auto-generate note immediately after transcription
      const noteResult = await api.generateMeetingNote(
        title,
        transcriptData,
        selectedTemplateId,
        recordingDuration,
        selectedLanguage,
        token
      );
      
      toast({
        title: "Erfolg",
        description: "Meeting wurde transkribiert und Notiz gespeichert.",
      });
      
      // Redirect to the note detail page
      const noteId = noteResult.meeting_note.id;
      navigate(`/meeting?note=${noteId}`);
      
    } catch (error) {
      console.error("Error processing recording:", error);
      toast({
        title: "Fehler",
        description: "Verarbeitung fehlgeschlagen.",
        variant: "destructive",
      });
    } finally {
      setIsProcessingRecording(false);
      setIsGeneratingNote(false);
    }
  };

  // Manual regenerate note (if user wants to regenerate with different template)
  const regenerateNote = async () => {
    if (!transcript) return;

    setIsGeneratingNote(true);
    try {
      const token = await getAccessToken();
      const result = await api.generateMeetingNote(
        meetingTitle || `Meeting - ${new Date().toLocaleDateString("de-DE")}`,
        transcript,
        selectedTemplateId,
        recordingDuration,
        selectedLanguage,
        token
      );
      setGeneratedNote(result.meeting_note.generated_note);
      toast({
        title: "Erfolg",
        description: "Notiz wurde neu generiert.",
      });
    } catch (error) {
      console.error("Error regenerating note:", error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht generiert werden.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingNote(false);
    }
  };

  // Reset state for new meeting
  const resetMeeting = () => {
    setTranscript(null);
    setGeneratedNote("");
    setMeetingTitle("");
    setRecordingDuration(0);
    audioChunksRef.current = [];
  };

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Template Manager functions
  const openCreateTemplateModal = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setTemplateContent("");
    setIsTemplateModalOpen(true);
  };

  const openEditTemplateModal = (template: MeetingTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateContent(template.content);
    setIsTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplate(null);
    setTemplateName("");
    setTemplateContent("");
  };

  const reloadTemplates = async () => {
    try {
      const token = await getAccessToken();
      const data = await api.getMeetingTemplates(token);
      setTemplates(data.templates || []);
    } catch (error) {
      console.error("Failed to reload templates:", error);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || !templateContent.trim()) {
      toast({
        title: "Fehler",
        description: "Name und Inhalt sind erforderlich.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingTemplate(true);
    try {
      const token = await getAccessToken();
      if (editingTemplate) {
        await api.updateMeetingTemplate(
          editingTemplate.id,
          { name: templateName, content: templateContent },
          token
        );
        toast({ title: "Erfolg", description: "Vorlage wurde aktualisiert." });
      } else {
        await api.createMeetingTemplate(templateName, templateContent, token);
        toast({ title: "Erfolg", description: "Vorlage wurde erstellt." });
      }
      closeTemplateModal();
      await reloadTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Fehler",
        description: "Vorlage konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    setIsDeletingTemplate(templateId);
    try {
      const token = await getAccessToken();
      await api.deleteMeetingTemplate(templateId, token);
      toast({ title: "Erfolg", description: "Vorlage wurde gelöscht." });
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(null);
      }
      await reloadTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast({
        title: "Fehler",
        description: "Vorlage konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingTemplate(null);
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  // Get template name
  const getTemplateName = (templateId: string | null) => {
    if (!templateId) return null;
    const template = templates.find((t) => t.id === templateId);
    return template?.name || null;
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden on mobile, same style as Assistant */}
        <div className="hidden lg:flex items-center justify-between p-4">
          <h2 className="text-lg font-bold text-slate-800">
            {APP_NAME} Meeting Assistent
          </h2>
          <div className="flex items-center gap-4">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-4 lg:p-8">
          {/* Show Note Detail View */}
          {selectedNote ? (
            <div className="max-w-4xl mx-auto space-y-6">
              {isLoadingNoteDetail ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {/* Note Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDuration(selectedNote.audio_duration)}
                      </span>
                      <span>
                        {new Date(selectedNote.created_at).toLocaleDateString("de-DE", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      {getTemplateName(selectedNote.template_id) && (
                        <span>Vorlage: {getTemplateName(selectedNote.template_id)}</span>
                      )}
                    </div>
                    <button
                      onClick={handleNewMeeting}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gray-100 text-gray-700 hover:bg-gray-200 h-9 px-4 py-2"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Neues Meeting
                    </button>
                  </div>

                  {/* Generated Note */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Meetingnotiz</h2>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 prose prose-sm max-w-none text-gray-700">
                      <Markdown remarkPlugins={[remarkGfm]}>
                        {selectedNote.generated_note || ""}
                      </Markdown>
                    </div>
                  </div>

                  {/* Transcript */}
                  {selectedNote.transcript && (
                    <div className="bg-white border border-gray-200 rounded-2xl p-6">
                      <h2 className="text-lg font-medium text-gray-900 mb-4">Transkript</h2>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto">
                        {selectedNote.transcript.segments &&
                        selectedNote.transcript.segments.length > 0 ? (
                          <div className="space-y-2">
                            {selectedNote.transcript.segments.map((segment, index) => (
                              <div key={index} className="text-sm">
                                {segment.speaker && (
                                  <span className="font-medium" style={{ color: APP_COLOR }}>
                                    {segment.speaker}:{" "}
                                  </span>
                                )}
                                <span className="text-gray-700">{segment.text}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {selectedNote.transcript.text}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            /* Show Recording UI */
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Recording Section */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Aufnahme</h2>
                
                <div className="flex flex-col items-center space-y-4">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessingRecording || (!isRecording && !selectedTemplateId)}
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all hover:opacity-90 ${
                      isRecording ? "animate-pulse" : ""
                    } ${isProcessingRecording || (!isRecording && !selectedTemplateId) ? "opacity-50 cursor-not-allowed" : ""}`}
                    style={{ backgroundColor: APP_COLOR }}
                  >
                    {isProcessingRecording ? (
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    ) : isRecording ? (
                      <Square className="w-8 h-8 text-white" />
                    ) : (
                      <Mic className="w-8 h-8 text-white" />
                    )}
                  </button>

                  <div className="text-center">
                    {isRecording && (
                      <p className="text-lg font-medium" style={{ color: APP_COLOR }}>
                        Aufnahme läuft... {formatDuration(recordingDuration)}
                      </p>
                    )}
                    {isProcessingRecording && (
                      <p className="text-gray-600">
                        {isGeneratingNote ? "Generiere Meetingnotiz..." : "Transkribiere Aufnahme..."}
                      </p>
                    )}
                    {!isRecording && !isProcessingRecording && !transcript && (
                      <p className="text-gray-500">
                        {selectedTemplateId 
                          ? "Klicken Sie auf das Mikrofon um die Aufnahme zu starten"
                          : "Bitte wählen Sie zuerst eine Vorlage aus"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Template Selection */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Vorlage auswählen</h2>
                
                <div className="relative">
                  <button
                    onClick={() => setIsTemplateDropdownOpen(!isTemplateDropdownOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <span className={selectedTemplate ? "text-gray-900" : "text-gray-500"}>
                      {isLoadingTemplates
                        ? "Lade Vorlagen..."
                        : selectedTemplate
                        ? selectedTemplate.name
                        : "Vorlage auswählen"}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isTemplateDropdownOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isTemplateDropdownOpen && (
                    <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => {
                            setSelectedTemplateId(template.id);
                            setIsTemplateDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 bg-white hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{template.name}</div>
                          <div className="text-sm text-gray-500 truncate">{template.content.slice(0, 80)}...</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Language Selection */}
                <h2 className="text-lg font-medium text-gray-900 mt-6 mb-4">Sprache</h2>
                <div className="relative">
                  <button
                    onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <span className="text-gray-900">
                      {languages.find(l => l.code === selectedLanguage)?.name || "Deutsch"}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isLanguageDropdownOpen && (
                    <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setSelectedLanguage(lang.code);
                            setIsLanguageDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                            selectedLanguage === lang.code ? "bg-gray-50 font-medium" : "bg-white"
                          }`}
                        >
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Template Manager Toggle */}
                {isSuperadmin && (
                  <button
                    onClick={() => setIsTemplateManagerOpen(!isTemplateManagerOpen)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mt-4 bg-transparent border-none"
                  >
                    <Settings2 className="w-4 h-4" />
                    Vorlagen verwalten
                    <ChevronDown className={`w-4 h-4 transition-transform ${isTemplateManagerOpen ? "rotate-180" : ""}`} />
                  </button>
                )}

                {/* Template Manager */}
                {isSuperadmin && isTemplateManagerOpen && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">Alle Vorlagen</span>
                        <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                          <span className="font-bold text-xs" style={{ color: APP_COLOR }}>
                            {templates.length} {templates.length === 1 ? "Vorlage" : "Vorlagen"}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={openCreateTemplateModal}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-9 px-4 py-2"
                        style={{ backgroundColor: APP_COLOR }}
                      >
                        <Plus size={16} className="mr-2" />
                        Neue Vorlage
                      </button>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                      <div className="grid grid-cols-[2fr_3fr_auto] bg-gray-50 text-gray-700 border-b border-gray-200">
                        <div className="p-4 font-medium">Name</div>
                        <div className="p-4 font-medium">Inhalt</div>
                        <div className="p-4 font-medium">Aktionen</div>
                      </div>

                      {templates.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          Keine Vorlagen vorhanden. Erstellen Sie Ihre erste Vorlage.
                        </div>
                      ) : (
                        templates.map((template) => (
                          <div
                            key={template.id}
                            className="grid grid-cols-[2fr_3fr_auto] border-b border-gray-200 last:border-b-0"
                          >
                            <div className="p-4 flex items-center">
                              <span className="font-medium text-gray-900">{template.name}</span>
                            </div>
                            <div className="p-4 flex items-center">
                              <span className="text-gray-600 truncate">{template.content.slice(0, 80)}...</span>
                            </div>
                            <div className="p-4 flex items-center gap-1">
                              <button
                                onClick={() => openEditTemplateModal(template)}
                                className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none text-gray-500 hover:text-gray-700 transition-colors"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteTemplate(template.id)}
                                disabled={isDeletingTemplate === template.id}
                                className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                              >
                                {isDeletingTemplate === template.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Transcript Display */}
              {transcript && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-gray-900">Transkript</h2>
                    {transcript.duration && (
                      <span className="text-sm text-gray-500">
                        Dauer: {formatDuration(Math.round(transcript.duration))}
                      </span>
                    )}
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto border border-gray-200">
                    {transcript.segments && transcript.segments.length > 0 ? (
                      <div className="space-y-2">
                        {transcript.segments.map((segment, index) => (
                          <div key={index} className="text-sm">
                            {segment.speaker && (
                              <span className="font-medium" style={{ color: APP_COLOR }}>
                                {segment.speaker}:{" "}
                              </span>
                            )}
                            <span className="text-gray-700">{segment.text}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">{transcript.text}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Regenerate Note Section - only show if transcript exists but no note yet (edge case) */}
              {transcript && !generatedNote && !isGeneratingNote && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Meetingnotiz neu generieren</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meeting Titel
                      </label>
                      <input
                        type="text"
                        value={meetingTitle}
                        onChange={(e) => setMeetingTitle(e.target.value)}
                        placeholder="z.B. Projektbesprechung Q1 2026"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                        style={{ "--tw-ring-color": APP_COLOR } as React.CSSProperties}
                      />
                    </div>

                    <button
                      onClick={regenerateNote}
                      disabled={isGeneratingNote || !meetingTitle}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2 w-full"
                      style={{ backgroundColor: APP_COLOR }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Meetingnotiz generieren
                    </button>
                  </div>
                </div>
              )}

              {/* Generated Note Display */}
              {generatedNote && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-gray-900">Generierte Meetingnotiz</h2>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={regenerateNote}
                        disabled={isGeneratingNote}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gray-100 text-gray-700 hover:bg-gray-200 h-8 px-3 py-1"
                      >
                        {isGeneratingNote ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Neu generieren
                          </>
                        )}
                      </button>
                      <button
                        onClick={resetMeeting}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-8 px-3 py-1"
                        style={{ backgroundColor: APP_COLOR }}
                      >
                        Neues Meeting
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 prose prose-sm max-w-none text-gray-700">
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {generatedNote}
                    </Markdown>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Template Create/Edit Modal */}
      {isTemplateModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTemplate ? "Vorlage bearbeiten" : "Neue Vorlage erstellen"}
              </h2>
              <button
                onClick={closeTemplateModal}
                disabled={isSavingTemplate}
                className="p-2 bg-white hover:bg-gray-100 rounded-lg disabled:opacity-50 border-none"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name der Vorlage
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="z.B. Standard Meeting Protokoll"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ "--tw-ring-color": APP_COLOR } as React.CSSProperties}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vorlageninhalt
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Verwenden Sie [Beschreibung] für variable Teile, die automatisch ausgefüllt werden.
                </p>
                <textarea
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  placeholder={`Beispiel:\n\nMeeting Titel: [Titel des Meetings]\n\nTeilnehmer:\n[Liste der Teilnehmer aus dem Gespräch]\n\nAgenda:\n[Besprochene Themen]\n\nBeschlüsse:\n[Getroffene Entscheidungen]\n\nNächste Schritte:\n[Action Items mit Verantwortlichen]`}
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 font-mono text-sm"
                  style={{ "--tw-ring-color": APP_COLOR } as React.CSSProperties}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={closeTemplateModal}
                disabled={isSavingTemplate}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gray-100 text-gray-700 hover:bg-gray-200 h-10 px-4 py-2"
              >
                Abbrechen
              </button>
              <button
                onClick={saveTemplate}
                disabled={isSavingTemplate || !templateName.trim() || !templateContent.trim()}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2"
                style={{ backgroundColor: APP_COLOR }}
              >
                {isSavingTemplate && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingTemplate ? "Speichern" : "Erstellen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}
